import React, { useState } from 'react';
import { FiChevronDown, FiHelpCircle } from 'react-icons/fi';
import useIsMobile from '../utils/useIsMobile';

const faqs = [
  {
    q: 'Is InternetTVService an internet or TV provider?',
    a: 'No. InternetTVService is an independent Authorized Dealer for Internet and TV services — not a provider, and not affiliated with or endorsed by any provider. We help you compare available plans and order service. The internet, TV, or wireless service itself is delivered, installed, and billed by the underlying provider under its own terms.',
  },
  {
    q: 'How does the process work?',
    a: 'It’s simple: (1) you call us or submit the contact form, (2) we check which providers and plans are available at your address, (3) we recommend options based on your speed needs and budget, and (4) we help you enroll and schedule installation with the provider.',
  },
  {
    q: 'How much do plans cost?',
    a: 'Pricing depends on your location and the provider you choose. Advertised prices are often promotional and apply for a limited time, after which standard rates apply. Taxes, surcharges, equipment, and installation fees may be extra. We’ll confirm pricing for your specific address before you order — no surprises.',
  },
  {
    q: 'Which areas do you serve?',
    a: 'We assist customers across the United States. Actual availability, speeds, and channel lineups depend on which providers cover your specific address, so we check serviceability for your location before recommending a plan.',
  },
  {
    q: 'What information do you need, and how is it used?',
    a: 'To help you, we typically need your name, phone, email, and service address. We use these details to check availability and recommend plans, and we share them with the providers or authorized partners needed to fulfill your request. We explain this fully in our Privacy Policy, and you can opt out of marketing contact at any time.',
  },
  {
    q: 'How do I get help, or change or cancel my service?',
    a: 'You can reach our team by phone or email for help choosing or setting up a plan. Once your service is active, billing, technical support, changes, and cancellations are handled directly by your provider under your service agreement. We’re happy to point you in the right direction.',
  },
];

const FaqItem = ({ item, isOpen, onToggle, isMobile }) => (
  <div style={{
    borderRadius: 16,
    border: `1.5px solid ${isOpen ? '#bfdbfe' : '#e8edf5'}`,
    background: '#fff', overflow: 'hidden',
    boxShadow: isOpen ? '0 8px 28px rgba(37,99,235,0.10)' : '0 2px 10px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.3s, border-color 0.3s',
  }}>
    <button
      onClick={onToggle}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 14,
        padding: isMobile ? '16px var(--cc-px)' : '20px 26px',
        background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}
    >
      <span style={{ flex: 1, fontWeight: 800, fontSize: isMobile ? '0.95rem' : '1.05rem', color: '#0f172a', letterSpacing: '-0.01em' }}>
        {item.q}
      </span>
      <FiChevronDown style={{
        width: 18, height: 18, color: '#94a3b8', flexShrink: 0,
        transition: 'transform 0.3s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
      }} />
    </button>
    <div style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', transition: 'grid-template-rows 0.35s ease' }}>
      <div style={{ overflow: 'hidden', minHeight: 0 }}>
        <p style={{
          margin: 0, padding: isMobile ? '0 var(--cc-px) 18px' : '0 26px 22px',
          color: '#475569', lineHeight: 1.8, fontSize: isMobile ? '0.9rem' : '0.98rem',
        }}>
          {item.a}
        </p>
      </div>
    </div>
  </div>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const isMobile = useIsMobile(768);

  return (
    <section style={{ padding: isMobile ? 'clamp(36px, 7vw, 56px) var(--cc-px)' : 'clamp(64px, 7vw, 100px) var(--cc-px)', background: '#f8f9fc', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 'var(--content-narrow)', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 40 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: '0.75rem', fontWeight: 700, color: '#2563eb', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>
            <FiHelpCircle style={{ width: 15, height: 15 }} /> FAQ
          </span>
          <h2 style={{ fontSize: 'clamp(1.7rem, 3vw, 2.6rem)', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
            Frequently Asked Questions
          </h2>
          <p style={{ color: '#64748b', fontSize: isMobile ? '0.92rem' : '1rem', lineHeight: 1.75, maxWidth: 560, margin: '12px auto 0' }}>
            Straight answers about who we are, how ordering works, and what to expect.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {faqs.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(prev => (prev === i ? null : i))}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
